import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Brand } from "../Brand";
import { useAuth } from "../../lib/auth";
import api, { formatCrypto } from "../../lib/api";
import InstaxIframe from "../InstaxIframe";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, XCircle, Clock3, RefreshCw,
  ShieldCheck, Info, History, LogIn, Copy, Ban,
} from "lucide-react";

const POLL_MS = 5000;
const shorten = (a) => (a && a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a || "—");

/** Buy Crypto purchase experience — /buy (MoonPay-style, custodial v1). */
export default function BuyCryptoApp() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [stage, setStage] = useState("form"); // form | creating | checkout | done
  const [order, setOrder] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // form state
  const [method, setMethod] = useState("card");
  const [fiat, setFiat] = useState("EUR");
  const [amount, setAmount] = useState("200");
  const [assetKey, setAssetKey] = useState(params.get("asset") || "BTC");
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    api.get("/buy/config").then((r) => {
      setCfg(r.data);
      const a = params.get("asset");
      if (a && r.data.assets?.some((x) => x.key === a)) setAssetKey(a);
    }).catch(() => toast.error("Couldn't load Buy Crypto configuration."));
  }, []); // eslint-disable-line

  // Resume an existing order (?order=… from the return URL)
  useEffect(() => {
    const oid = params.get("order");
    if (!oid || authLoading || !user) return;
    api.get(`/buy/orders/${oid}`).then((r) => {
      setOrder(r.data);
      setStage(["completed", "failed", "cancelled", "refunded"].includes(r.data.status) ? "done" : "checkout");
    }).catch(() => {});
  }, [params, user, authLoading]);

  const methodCfg = useMemo(() => cfg?.methods?.find((m) => m.key === method), [cfg, method]);
  useEffect(() => {
    if (methodCfg && !methodCfg.fiats.includes(fiat)) setFiat(methodCfg.fiats[0]);
  }, [methodCfg]); // eslint-disable-line

  // Debounced live quote
  const quoteTimer = useRef(null);
  useEffect(() => {
    if (!cfg) return;
    const amt = parseFloat(amount);
    setQuote(null);
    setQuoteErr("");
    if (!amt || amt <= 0) return;
    clearTimeout(quoteTimer.current);
    setQuoting(true);
    quoteTimer.current = setTimeout(() => {
      api.post("/buy/quote", { fiat, fiat_amount: amt, asset_key: assetKey, method })
        .then((r) => setQuote(r.data))
        .catch((e) => setQuoteErr(e?.response?.data?.detail || "Quote unavailable."))
        .finally(() => setQuoting(false));
    }, 350);
    return () => clearTimeout(quoteTimer.current);
  }, [cfg, fiat, amount, assetKey, method]);

  const startPurchase = async () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/buy?asset=${assetKey}`)}`);
      return;
    }
    setStage("creating");
    try {
      const r = await api.post("/buy/orders", {
        fiat, fiat_amount: parseFloat(amount), asset_key: assetKey, method,
      });
      setOrder(r.data);
      setStage("checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't start the purchase.");
      setStage("form");
    }
  };

  // Poll order status while in checkout
  const refreshOrder = useCallback(async () => {
    if (!order?.id) return;
    try {
      const r = await api.get(`/buy/orders/${order.id}`);
      setOrder(r.data);
      if (["completed", "failed", "cancelled", "refunded"].includes(r.data.status)) setStage("done");
    } catch {}
  }, [order?.id]);
  useEffect(() => {
    if (stage !== "checkout") return;
    const id = setInterval(refreshOrder, POLL_MS);
    return () => clearInterval(id);
  }, [stage, refreshOrder]);

  const reset = () => { setOrder(null); setStage("form"); setQuote(null); navigate("/buy", { replace: true }); };

  return (
    <div className="min-h-screen bg-background" data-testid="buy-crypto-app">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="buy-logo-btn"><Brand size={26} /></button>
          <div className="flex items-center gap-2">
            {user ? (
              <Button variant="ghost" size="sm" className="rounded-xl gap-1.5"
                onClick={() => setShowHistory((v) => !v)} data-testid="buy-history-btn">
                <History className="w-4 h-4" /> My purchases
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="rounded-xl gap-1.5"
                onClick={() => navigate(`/login?next=${encodeURIComponent("/buy")}`)}
                data-testid="buy-login-btn">
                <LogIn className="w-4 h-4" /> Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        {showHistory && user && <HistoryPanel onOpen={(o) => {
          setOrder(o); setShowHistory(false);
          setStage(["completed", "failed", "cancelled", "refunded"].includes(o.status) ? "done" : "checkout");
        }} />}

        {stage === "form" && (
          <FormStage cfg={cfg} method={method} setMethod={setMethod} fiat={fiat} setFiat={setFiat}
            amount={amount} setAmount={setAmount} assetKey={assetKey} setAssetKey={setAssetKey}
            quote={quote} quoteErr={quoteErr} quoting={quoting} methodCfg={methodCfg}
            onContinue={startPurchase} user={user} />
        )}

        {stage === "creating" && (
          <div className="flex flex-col items-center justify-center py-32" data-testid="buy-creating">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <div className="font-display font-semibold">Preparing your secure checkout…</div>
            <p className="text-sm text-muted-foreground mt-1">This usually takes a couple of seconds.</p>
          </div>
        )}

        {stage === "checkout" && order && (
          <CheckoutStage order={order} onRefresh={refreshOrder} onCancel={reset} />
        )}

        {stage === "done" && order && (
          <DoneStage order={order} onNew={reset} onRetry={() => { setStage("form"); }} />
        )}
      </main>
    </div>
  );
}

/* ---------------- Form stage ---------------- */
function FormStage({ cfg, method, setMethod, fiat, setFiat, amount, setAmount,
                     assetKey, setAssetKey, quote, quoteErr, quoting, methodCfg, onContinue, user }) {
  const assets = cfg?.assets || [];
  const methods = cfg?.methods || [];
  const asset = assets.find((a) => a.key === assetKey);
  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3">
        <h1 className="xch-headline text-3xl sm:text-4xl mb-6" data-testid="buy-form-title">Buy crypto</h1>

        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Payment method</div>
        <div className="grid sm:grid-cols-3 gap-2 mb-6" data-testid="buy-method-picker">
          {methods.map((m) => (
            <button key={m.key} onClick={() => setMethod(m.key)}
              className={`rounded-xl border p-3 text-left transition-colors ${method === m.key
                ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
              data-testid={`buy-method-${m.key}`}>
              <div className="font-display font-semibold text-sm">{m.label}</div>
              <div className="text-[11px] text-muted-foreground">{m.region} · {m.settlement}</div>
            </button>
          ))}
        </div>

        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">You pay</div>
        <div className="flex gap-2 mb-6">
          <Input type="number" inputMode="decimal" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="text-lg font-semibold h-12 rounded-xl flex-1" data-testid="buy-amount-input" />
          <select value={fiat} onChange={(e) => setFiat(e.target.value)}
            className="h-12 rounded-xl border border-input bg-background px-3 font-semibold text-sm"
            data-testid="buy-fiat-select">
            {(methodCfg?.fiats || ["EUR"]).map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {methodCfg && (
          <div className="text-xs text-muted-foreground -mt-4 mb-6">
            Limits: {methodCfg.min}–{methodCfg.max} {fiat} per purchase
          </div>
        )}

        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">You receive</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="buy-asset-picker">
          {assets.map((a) => (
            <button key={a.key} onClick={() => setAssetKey(a.key)}
              className={`rounded-xl border p-3 text-left transition-colors ${assetKey === a.key
                ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
              data-testid={`buy-asset-${a.key}`}>
              <div className="font-display font-semibold text-sm">{a.symbol}</div>
              <div className="text-[11px] text-muted-foreground truncate">{a.network}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Quote panel */}
      <div className="lg:col-span-2">
        <div className="xch-card p-6 sticky top-24" data-testid="buy-quote-panel">
          <div className="font-display font-semibold mb-4">Order preview</div>
          {quoting && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center" data-testid="buy-quote-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching live rate…
            </div>
          )}
          {!quoting && quoteErr && (
            <div className="text-sm text-rose-600 py-6 text-center" data-testid="buy-quote-error">{String(quoteErr)}</div>
          )}
          {!quoting && quote && (
            <>
              <div className="text-3xl font-display font-bold" data-testid="buy-quote-crypto">
                ≈ {formatCrypto(quote.est_crypto)} <span className="text-lg">{quote.symbol}</span>
              </div>
              <div className="text-xs text-muted-foreground mb-4">{asset?.name} on {quote.network}</div>
              <dl className="space-y-2 text-sm border-t border-border pt-4">
                <Row k="Exchange rate" v={`1 ${quote.symbol} ≈ ${Number(quote.rate).toLocaleString()} ${quote.fiat}`} tid="buy-quote-rate" />
                <Row k="Provider fee" v={quote.fee_pct > 0 ? `${quote.fee_amount} ${quote.fiat} (${quote.fee_pct}%)` : "Included in checkout rate"} tid="buy-quote-fee" />
                <Row k="Network" v={quote.network} tid="buy-quote-network" />
                <Row k="Settlement" v={quote.settlement} tid="buy-quote-settlement" />
                <Row k="Arrival" v={quote.arrival} tid="buy-quote-arrival" />
              </dl>
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-secondary/60 rounded-lg p-3 mt-4"
                data-testid="buy-quote-disclaimer">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{quote.disclaimer}
              </div>
            </>
          )}
          <Button className="w-full rounded-xl h-12 mt-5 gap-2" disabled={!quote || quoting}
            onClick={onContinue} data-testid="buy-continue-btn">
            {user ? "Continue to secure checkout" : "Sign in to continue"} <ArrowRight className="w-4 h-4" />
          </Button>
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground mt-3">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" /> KYC &amp; payment handled in the provider's secure checkout
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, tid }) => (
  <div className="flex justify-between gap-3" data-testid={tid}>
    <dt className="text-muted-foreground">{k}</dt>
    <dd className="font-medium text-right">{v}</dd>
  </div>
);

/* ---------------- Checkout stage ---------------- */
function CheckoutStage({ order, onRefresh, onCancel }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="buy-checkout-stage">
      <div className="lg:col-span-2 xch-card overflow-hidden">
        <InstaxIframe url={order.checkout_url} title="Secure crypto checkout" testid="buy-instax-iframe" />
      </div>
      <div>
        <div className="xch-card p-6 sticky top-24">
          <div className="flex items-center gap-2 mb-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
            </span>
            <div className="font-display font-semibold text-sm" data-testid="buy-checkout-status">
              {order.status === "processing" ? "Payment processing…" : "Awaiting payment"}
            </div>
          </div>
          <dl className="space-y-2 text-sm">
            <Row k="Order" v={order.id} tid="buy-checkout-ref" />
            <Row k="You pay" v={`${order.fiat_amount} ${order.fiat}`} tid="buy-checkout-pay" />
            <Row k="You receive" v={`≈ ${formatCrypto(order.est_crypto)} ${order.symbol}`} tid="buy-checkout-receive" />
            <Row k="Network" v={order.network} tid="buy-checkout-network" />
            <Row k="Method" v={order.method_label} tid="buy-checkout-method" />
          </dl>
          <div className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
            Complete the steps in the secure checkout. This page updates automatically —
            you can safely come back later from <b>My purchases</b>.
          </div>
          <div className="flex gap-2 mt-5">
            <Button variant="outline" size="sm" className="rounded-xl gap-1.5 flex-1" onClick={onRefresh}
              data-testid="buy-checkout-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" className="rounded-xl flex-1" onClick={onCancel}
              data-testid="buy-checkout-back">
              <ArrowLeft className="w-3.5 h-3.5" /> New purchase
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Done stage ---------------- */
const DONE_META = {
  completed: { icon: CheckCircle2, tone: "text-emerald-600", bg: "bg-emerald-50", title: "Purchase complete" },
  failed:    { icon: XCircle, tone: "text-rose-600", bg: "bg-rose-50", title: "Payment failed" },
  cancelled: { icon: Ban, tone: "text-slate-500", bg: "bg-slate-100", title: "Purchase cancelled" },
  refunded:  { icon: RefreshCw, tone: "text-sky-600", bg: "bg-sky-50", title: "Purchase refunded" },
};

function DoneStage({ order, onNew, onRetry }) {
  const meta = DONE_META[order.status] || DONE_META.cancelled;
  const Icon = meta.icon;
  const copy = () => { navigator.clipboard?.writeText(order.id); toast.success("Reference copied"); };
  return (
    <div className="max-w-lg mx-auto" data-testid="buy-done-stage">
      <div className="xch-card p-8 text-center">
        <div className={`w-16 h-16 rounded-2xl ${meta.bg} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-8 h-8 ${meta.tone}`} />
        </div>
        <h2 className="font-display font-bold text-xl" data-testid="buy-done-title">{meta.title}</h2>
        {order.status === "completed" && (
          <p className="text-sm text-muted-foreground mt-1">
            {formatCrypto(order.est_crypto)} {order.symbol} has been credited to your Xchangely account.
          </p>
        )}
        {order.status === "failed" && order.failure_reason && (
          <p className="text-sm text-rose-600 mt-1" data-testid="buy-done-reason">{order.failure_reason}</p>
        )}
        <dl className="space-y-2 text-sm text-left border-t border-border mt-6 pt-5">
          <Row k="Reference" v={<button onClick={copy} className="inline-flex items-center gap-1 hover:text-primary" data-testid="buy-done-copy">{order.id} <Copy className="w-3 h-3" /></button>} tid="buy-done-ref" />
          <Row k="Asset" v={`${order.symbol} · ${order.network}`} tid="buy-done-asset" />
          <Row k="Amount paid" v={`${order.fiat_amount} ${order.fiat}`} tid="buy-done-amount" />
          <Row k="Payment method" v={order.method_label} tid="buy-done-method" />
          <Row k="Custody wallet" v={shorten(order.destination_address)} tid="buy-done-destination" />
          {order.completed_at && <Row k="Completed" v={new Date(order.completed_at).toLocaleString()} tid="buy-done-when" />}
        </dl>
        <div className="flex gap-2 mt-7">
          {order.status !== "completed" && (
            <Button variant="outline" className="rounded-xl flex-1 gap-1.5" onClick={onRetry} data-testid="buy-done-retry">
              <RefreshCw className="w-4 h-4" /> Try again
            </Button>
          )}
          <Button className="rounded-xl flex-1 gap-1.5" onClick={onNew} data-testid="buy-done-new">
            Buy more crypto <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- History ---------------- */
const HIST_TONE = {
  completed: "text-emerald-600", failed: "text-rose-600", cancelled: "text-slate-500",
  processing: "text-amber-600", initiated: "text-sky-600", refunded: "text-sky-600",
};

function HistoryPanel({ onOpen }) {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/buy/orders").then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([]));
  }, []);
  return (
    <div className="xch-card p-5 mb-8" data-testid="buy-history-panel">
      <div className="font-display font-semibold text-sm mb-3 flex items-center gap-2">
        <History className="w-4 h-4 text-primary" /> My purchases
      </div>
      {!orders && <div className="text-sm text-muted-foreground py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
      {orders && orders.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center" data-testid="buy-history-empty">No purchases yet.</div>}
      {orders && orders.length > 0 && (
        <div className="divide-y divide-border">
          {orders.map((o) => (
            <button key={o.id} onClick={() => onOpen(o)}
              className="w-full flex items-center justify-between py-2.5 text-left hover:bg-secondary/50 rounded-lg px-2 transition-colors"
              data-testid={`buy-history-row-${o.id}`}>
              <div>
                <div className="text-sm font-medium">{o.fiat_amount} {o.fiat} → {o.symbol}</div>
                <div className="text-[11px] text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.method_label}</div>
              </div>
              <div className={`text-xs font-semibold capitalize ${HIST_TONE[o.status] || ""}`}>{o.status}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
